import React from 'react'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { alpha } from '@mui/material/styles'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../generic/systemValues/systemValueLabel'
import { PILL_ICON } from './DeliveryCells'
import { DELIVERY_STATUSES, DELIVERY_STATUS_PILL } from './deliveryStatusPresentation'

// Contract §2 -- the count Sun publishes for each status. Jupiter reads the four; it never subtracts
// (DL-11/INV-I: "on time" is `onTimeFabricationOrderCount`, not planned minus the other three).
const COUNT_FIELD_BY_STATUS = {
  LATE: 'lateFabricationOrderCount',
  ON_TIME: 'onTimeFabricationOrderCount',
  EARLY: 'earlyFabricationOrderCount',
  NO_TARGET_DATE: 'withoutTargetDateFabricationOrderCount'
}

// MUI Button colours for the palette the pills used ('default' has no Button colour).
const BUTTON_COLOR = { error: 'error', success: 'success', info: 'info', default: 'inherit' }

/**
 * Story 175, CU8/CU9 -- the four counters over the details table: RETRASO · A TIEMPO · ADELANTO · Sin
 * fecha, with the counts of `planMetrics/{id}/deliveries` (RN32). Pressing one asks the caller to toggle that
 * status; each reports its state through `aria-pressed` (contract §7).
 * Story 177 (PO on the build, 02/10): they look like buttons, so it is clear they can be pressed; the
 * "<status> · n of N FOs" line goes (the table shows it), and "Clear filter" sits on the same row as them.
 */
const DeliveryStatusCounters = ({ counts, activeStatus = null, onToggle = () => {}, onClear = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const labelOf = (status) => systemValueLabel(t_, 'DeliveryStatus', status)
  return (
    <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap data-testid='delivery-counters' sx={{ marginBottom: 1 }}>
      {DELIVERY_STATUSES.map((status) => {
        const pressed = activeStatus === status
        const color = BUTTON_COLOR[DELIVERY_STATUS_PILL[status].color] ?? 'inherit'
        return (
          <Button
            key={status}
            size='small'
            color={color}
            variant={pressed ? 'contained' : 'outlined'}
            startIcon={PILL_ICON[status]}
            onClick={() => onToggle(status)}
            aria-pressed={pressed ? 'true' : 'false'}
            data-status={status}
            data-testid={'delivery-counter-' + status}
            sx={(theme) => ({
              borderRadius: 1.5,
              paddingX: 1.5,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: pressed ? undefined : `0 1px 2px ${alpha(theme.palette.common.black, 0.35)}`
            })}
          >
            {`${counts[COUNT_FIELD_BY_STATUS[status]]} ${labelOf(status)}`}
          </Button>
        )
      })}
      {activeStatus !== null
        ? <Button size='small' onClick={onClear} data-testid='delivery-filter-row'>{t_('Clear filter')}</Button>
        : null}
    </Stack>
  )
}

export default DeliveryStatusCounters
